/* RESPONSIBLE TEAM: team-app-security */

import Model, { attr, belongsTo } from '@ember-data/model';
import type Admin from 'embercom/models/admin';
import { ALL_SEAT_LABELS, type SeatType } from 'embercom/lib/settings/seats/constants';
import { formatList } from 'embercom/helpers/format-list-helper';
import { isEqual } from 'underscore';

export default class AppAdminEvent extends Model {
  @attr('string') declare type: string;
  @attr() declare value: any;
  @attr('string') declare app_id: number;
  @attr('string') declare admin_id: number;
  @attr('string') declare admin_ip: string;
  @attr('date') declare created_at: string;
  @attr('number') declare impersonation_id: number | null;

  @belongsTo('admin', { async: false }) declare admin: Admin;

  get humanType() {
    return this.type.replace('AppAdminEvents::', '').replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  get isAdminDeletion() {
    return this.type === 'AppAdminEvents::AdminDeletion';
  }

  get isAdminImpersonationEnd() {
    return this.type === 'AppAdminEvents::AdminImpersonationEnd';
  }

  get isAdminImpersonationStart() {
    return this.type === 'AppAdminEvents::AdminImpersonationStart';
  }

  get isAdminImpersonationConsentApproved() {
    return this.type === 'AppAdminEvents::AdminImpersonationConsentApproved';
  }

  get isAdminImpersonationConsentRevoked() {
    return this.type === 'AppAdminEvents::AdminImpersonationConsentRevoked';
  }

  get isOauthTokenRevocation() {
    return this.type === 'AppAdminEvents::OauthTokenRevocation';
  }

  get isAdminInviteChange() {
    return this.type === 'AppAdminEvents::AdminInviteChange';
  }

  get isAdminInviteCreation() {
    return this.type === 'AppAdminEvents::AdminInviteCreation';
  }

  get isAdminBulkInviteCreation() {
    return this.type === 'AppAdminEvents::AdminBulkInviteCreation';
  }

  get isAdminInviteDeletion() {
    return this.type === 'AppAdminEvents::AdminInviteDeletion';
  }

  get _isRoleChanged() {
    return this.value.before.role && this.value.after.role;
  }

  get _isRoleAssigned() {
    return !this.value.before.role && this.value.after.role;
  }

  get _isRoleRemoved() {
    return this.value.before.role && !this.value.after.role;
  }

  get isAdminInviteRoleChange() {
    return this.isAdminInviteChange && this._isRoleChanged;
  }

  get isAdminInviteRoleAssign() {
    return this.isAdminInviteChange && this._isRoleAssigned;
  }

  get isAdminInviteRoleRemove() {
    return this.isAdminInviteChange && this._isRoleRemoved;
  }

  get isAdminLoginFailure() {
    return this.type === 'AppAdminEvents::AdminLoginFailure';
  }

  get isAdminLoginSuccess() {
    return this.type === 'AppAdminEvents::AdminLoginSuccess';
  }

  get isAdminLoginAttemptsReset() {
    return this.type === 'AppAdminEvents::AdminLoginAttemptsReset';
  }

  get isAdminTwoFactorLoginFailure() {
    return this.type === 'AppAdminEvents::AdminTwoFactorLoginFailure';
  }

  get isAdminLogout() {
    return this.type === 'AppAdminEvents::AdminLogout';
  }

  get isInboxAccessRevoked() {
    return (
      this.type === 'AppAdminEvents::InboxAccessChange' &&
      this.value.before.has_inbox_access === true &&
      this.value.after.has_inbox_access === false
    );
  }

  get isInboxAccessGranted() {
    return (
      this.type === 'AppAdminEvents::InboxAccessChange' &&
      this.value.before.has_inbox_access === false &&
      this.value.after.has_inbox_access === true
    );
  }

  get isAdminPermissionChange() {
    return this.type === 'AppAdminEvents::AdminPermissionChange';
  }

  get isAdminRoleChange() {
    return this.isAdminPermissionChange && this._isRoleChanged;
  }

  get isAdminRoleAssign() {
    return this.isAdminPermissionChange && this._isRoleAssigned;
  }

  get isAdminRoleRemove() {
    return this.isAdminPermissionChange && this._isRoleRemoved;
  }

  get isAdminAwayModeChange() {
    return (
      this.type === 'AppAdminEvents::AdminAwayModeChange' &&
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      (this.value.auto_changed === false || !this.value.source.includes('auto away status worker'))
    );
  }

  get isAdminAwayModeAutoChange() {
    return (
      this.type === 'AppAdminEvents::AdminAwayModeChange' &&
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      (this.value.auto_changed || this.value.source.includes('auto away status worker'))
    );
  }

  get isAppAdminJoin() {
    return this.type === 'AppAdminEvents::AppAdminJoin';
  }

  get isAppAuthenticationMethodChange() {
    return this.type === 'AppAdminEvents::AppAuthenticationMethodChange';
  }

  get isAppDataDeletion() {
    return this.type === 'AppAdminEvents::AppDataDeletion';
  }

  get isAppGoogleSsoDomainChange() {
    return this.type === 'AppAdminEvents::AppGoogleSsoDomainChange';
  }

  get isAppIdentityVerificationChange() {
    return this.type === 'AppAdminEvents::AppIdentityVerificationChange';
  }

  get isAppNameChange() {
    return this.type === 'AppAdminEvents::AppNameChange';
  }

  get isAppOutboundAddressChange() {
    return this.type === 'AppAdminEvents::AppOutboundAddressChange';
  }

  get isAppPackageTokenRegeneration() {
    return this.type === 'AppAdminEvents::AppPackageTokenRegeneration';
  }

  get isAppTimezoneChange() {
    return this.type === 'AppAdminEvents::AppTimezoneChange';
  }

  get isAppWebhookCreation() {
    return this.type === 'AppAdminEvents::AppWebhookCreation';
  }

  get isAppWebhookDeletion() {
    return this.type === 'AppAdminEvents::AppWebhookDeletion';
  }

  get isBulkDataExportCreation() {
    return this.type === 'AppAdminEvents::BulkDataExportCreation';
  }

  get isBulkDataExportRun() {
    return this.type === 'AppAdminEvents::BulkDataExportRun';
  }

  get isBulkExport() {
    return this.type === 'AppAdminEvents::BulkExport';
  }

  get isBulkDelete() {
    return this.type === 'AppAdminEvents::BulkDelete';
  }

  get isMessageDeletion() {
    return this.type === 'AppAdminEvents::MessageDeletion';
  }

  get isRulesetActivation() {
    return this.type === 'AppAdminEvents::RulesetActivation';
  }

  get isRulesetDeletion() {
    return this.type === 'AppAdminEvents::RulesetDeletion';
  }

  get isRulesetCreation() {
    return this.type === 'AppAdminEvents::RulesetCreation';
  }

  get isWorkflowCreation() {
    return this.type === 'AppAdminEvents::WorkflowCreation';
  }

  get isWorkflowDeletion() {
    return this.type === 'AppAdminEvents::WorkflowDeletion';
  }

  get isWorkflowUpdate() {
    return this.type === 'AppAdminEvents::WorkflowUpdate';
  }

  get isWorkflowStatusChange() {
    return this.type === 'AppAdminEvents::WorkflowStatusChange';
  }

  get isArticleCreation() {
    return this.type === 'AppAdminEvents::ArticleCreation';
  }

  get isArticleContentUpdate() {
    return this.type === 'AppAdminEvents::ArticleContentUpdate';
  }

  get isArticleDeletion() {
    return this.type === 'AppAdminEvents::ArticleDeletion';
  }

  get isArticleContentCreation() {
    return this.type === 'AppAdminEvents::ArticleContentCreation';
  }

  get isArticleContentDeletion() {
    return this.type === 'AppAdminEvents::ArticleContentDeletion';
  }

  get isArticleContentPublished() {
    return this.type === 'AppAdminEvents::ArticleContentPublished';
  }

  get isArticleContentUnpublished() {
    return this.type === 'AppAdminEvents::ArticleContentUnpublished';
  }

  get isMessageStateChange() {
    return this.type === 'AppAdminEvents::MessageStateChange';
  }

  get isCampaignDeletion() {
    return this.type === 'AppAdminEvents::CampaignDeletion';
  }

  get isCampaignStateChange() {
    return this.type === 'AppAdminEvents::CampaignStateChange';
  }

  get isAppTeamCreation() {
    return this.type === 'AppAdminEvents::AppTeamCreation';
  }

  get isAppTeamDeletion() {
    return this.type === 'AppAdminEvents::AppTeamDeletion';
  }

  get isAppTeamMembershipModification() {
    return this.type === 'AppAdminEvents::AppTeamMembershipModification';
  }

  get isAppPackageInstallation() {
    return this.type === 'AppAdminEvents::AppPackageInstallation';
  }

  get isAppPackageUninstallation() {
    return this.type === 'AppAdminEvents::AppPackageUninstallation';
  }

  get isAppDataExport() {
    return this.type === 'AppAdminEvents::AppDataExport';
  }

  get isHelpCenterSettingsChange() {
    return this.type === 'AppAdminEvents::HelpCenterSettingsChange';
  }

  get isRoleChange() {
    return this.type === 'AppAdminEvents::RoleChange';
  }

  get isRoleCreation() {
    return this.type === 'AppAdminEvents::RoleCreation';
  }

  get isRoleDeletion() {
    return this.type === 'AppAdminEvents::RoleDeletion';
  }

  get isAdminProvisioned() {
    return this.type === 'AppAdminEvents::AdminProvisioned';
  }

  get isAdminDeprovisioned() {
    return this.type === 'AppAdminEvents::AdminDeprovisioned';
  }

  get isSeatAssigned() {
    return (
      this.type === 'AppAdminEvents::SeatChange' &&
      this.value.after.some((seat: string) => !this.value.before.includes(seat))
    );
  }

  get isSeatRevoked() {
    return (
      this.type === 'AppAdminEvents::SeatChange' &&
      this.value.before.some((seat: string) => !this.value.after.includes(seat))
    );
  }

  get isSeatChange() {
    return (
      this.type === 'AppAdminEvents::SeatChange' && !this.isSeatAssigned && !this.isSeatRevoked
    );
  }

  get assignedSeatLabel() {
    return this.formatSeatLabelChange(this.value.before, this.value.after);
  }

  get revokedSeatLabel() {
    return this.formatSeatLabelChange(this.value.after, this.value.before);
  }

  formatSeatLabelChange(originalSeats: string[], updatedSeats: string[]) {
    let seatLabels = updatedSeats
      .filter((seat) => !originalSeats.includes(seat))
      .map((seatType: SeatType) => ALL_SEAT_LABELS[seatType]);

    return formatList([seatLabels], { type: 'conjunction' });
  }

  get isOfficeHoursChange() {
    return this.type === 'AppAdminEvents::OfficeHoursChange';
  }

  get isTopicCreation() {
    return this.type === 'AppAdminEvents::ConversationTopicCreation';
  }

  get isTopicChange() {
    return this.type === 'AppAdminEvents::ConversationTopicChange';
  }

  get isTopicDeletion() {
    return this.type === 'AppAdminEvents::ConversationTopicDeletion';
  }

  get isTopicCurationSetLive() {
    return this.type === 'AppAdminEvents::TopicCurationSetLive';
  }

  get isConversationPartDeletion() {
    return this.type === 'AppAdminEvents::ConversationPartDeletion';
  }

  get isConversationPartRedaction() {
    return this.type === 'AppAdminEvents::ConversationPartRedaction';
  }

  get isInboundConversationsChange() {
    return this.type === 'AppAdminEvents::InboundConversationsChange';
  }

  get isMessengerSpacesChange() {
    return this.type === 'AppAdminEvents::MessengerSpacesChange';
  }

  get isWelcomeMessageChange() {
    return this.type === 'AppAdminEvents::WelcomeMessageChange';
  }

  get isStartConversationButtonTextChange() {
    return this.type === 'AppAdminEvents::StartConversationButtonTextChange';
  }

  get isLookAndFeelChange() {
    return this.type === 'AppAdminEvents::MessengerLookAndFeelChange';
  }

  get isBrandSettingsChange() {
    return this.type === 'AppAdminEvents::BrandSettingsChange';
  }

  // When these events were originally created they were named SearchBrowse but changed to ArticlesInMessenger later
  // So to allow historical data to still display correctly check for both event type names
  get isSearchBrowseEnabledChange() {
    return (
      this.type === 'AppAdminEvents::SearchBrowseEnabledChange' ||
      this.type === 'AppAdminEvents::ArticlesInMessengerEnabledChange'
    );
  }

  get isSearchBrowseRequiredChange() {
    return (
      this.type === 'AppAdminEvents::SearchBrowseRequiredChange' ||
      this.type === 'AppAdminEvents::MessengerSearchRequiredChange'
    );
  }

  get isTemporaryExpectationChange() {
    return this.type === 'AppAdminEvents::TemporaryExpectationChange';
  }

  get isTemporaryExpectationMessagesChange() {
    return this.type === 'AppAdminEvents::TemporaryExpectationMessagesChange';
  }

  get isUpfrontEmailCollectionChange() {
    return this.type === 'AppAdminEvents::UpfrontEmailCollectionChange';
  }

  get isPrivacyPolicyNoticeChange() {
    return this.type === 'AppAdminEvents::PrivacyPolicyNoticeChange';
  }

  get isPrivacyPolicyNoticeContentsChange() {
    return this.type === 'AppAdminEvents::PrivacyPolicyNoticeContentsChange';
  }
  get isSecuritySettingChange() {
    return this.type === 'AppAdminEvents::SecuritySettingsChange';
  }

  get isMaliciousDomainsSettingChange() {
    return this.type === 'AppAdminEvents::MaliciousDomainsSettingChange';
  }

  get isTrustedDomainsSettingChange() {
    return this.type === 'AppAdminEvents::TrustedDomainsSettingChange';
  }

  get isTwoFactorAuthChange() {
    return this.type === 'AppAdminEvents::AdminTwoFactorAuthChange';
  }

  get isEmailSignatureChange() {
    return this.type === 'AppAdminEvents::EmailSignatureChange';
  }

  get isRecoveryCodeSent() {
    return this.type === 'AppAdminEvents::RecoveryCodeSent';
  }

  get isBillingAddresseesChange() {
    return this.type === 'AppAdminEvents::BillingAddresseesChange';
  }

  get isBillingAddresseesBounced() {
    return this.type === 'AppAdminEvents::BillingAddresseesBouncedAndRemoved';
  }

  get isReportCreatedEvent() {
    return this.type === 'AppAdminEvents::CustomReportCreate';
  }

  get isReportUpdateEvent() {
    return this.type === 'AppAdminEvents::CustomReportUpdate';
  }

  get isReportDeleteEvent() {
    return this.type === 'AppAdminEvents::CustomReportDelete';
  }

  get isReportingFolderCreateEvent() {
    return this.type === 'AppAdminEvents::ReportingFolderCreate';
  }

  get isReportingFolderDeleteEvent() {
    return this.type === 'AppAdminEvents::ReportingFolderDelete';
  }

  get isReportingFolderNameChange() {
    return this.type === 'AppAdminEvents::ReportingFolderNameChange';
  }

  get isReportingFolderPositionChange() {
    return this.type === 'AppAdminEvents::ReportingFolderPositionChange';
  }

  get isReportingFolderReportsChange() {
    return this.type === 'AppAdminEvents::ReportingFolderReportsChange';
  }

  get isReportingFolderPinChange() {
    return this.type === 'AppAdminEvents::ReportingFolderPinChange';
  }

  get isReportingCsvExport() {
    return this.type === 'AppAdminEvents::ReportingCsvExport';
  }

  get isReportSnapshotCreateEvent() {
    return this.type === 'AppAdminEvents::ReportSnapshotCreate';
  }

  get isReportScheduledShareCreate() {
    return this.type === 'AppAdminEvents::ReportScheduledShareCreate';
  }

  get isReportScheduledShareUpdate() {
    return this.type === 'AppAdminEvents::ReportScheduledShareUpdate';
  }

  get isReportScheduledShareDelete() {
    return this.type === 'AppAdminEvents::ReportScheduledShareDelete';
  }

  get isReportAccessCreate() {
    return this.type === 'AppAdminEvents::ReportAccessCreate';
  }

  get isReportAccessDelete() {
    return this.type === 'AppAdminEvents::ReportAccessDelete';
  }

  get isReportAccessUpdate() {
    return this.type === 'AppAdminEvents::ReportAccessUpdate';
  }

  get hasPermissionChangeDetails() {
    return (
      this.isAdminProvisioned ||
      this.isRoleDeletion ||
      this.isRoleCreation ||
      this.isRoleChange ||
      this.isAdminInviteRoleChange ||
      this.isAdminInviteRoleAssign ||
      this.isAdminInviteRoleRemove ||
      this.isAdminInviteChange ||
      this.isAdminInviteCreation ||
      this.isAdminBulkInviteCreation ||
      this.isAdminRoleChange ||
      this.isAdminRoleAssign ||
      this.isAdminRoleRemove ||
      this.isAdminPermissionChange
    );
  }

  get hasSeatChangeDetails() {
    return this.isSeatAssigned || this.isSeatRevoked || this.isSeatChange;
  }

  get hasTopicChangeDetails() {
    return this.isTopicCreation || this.isTopicChange || this.isTopicDeletion;
  }

  get isEmailSetLive() {
    return this.isRulesetActivation && this.value.content_type === 'email';
  }

  get isAdminUnauthorizedSignInMethod() {
    return this.type === 'AppAdminEvents::AdminUnauthorizedSignInMethod';
  }

  get isAdminPasswordResetRequest() {
    return this.type === 'AppAdminEvents::AdminPasswordResetRequest';
  }

  get isAdminPasswordResetSuccess() {
    return this.type === 'AppAdminEvents::AdminPasswordResetSuccess';
  }

  get isAdminAssignmentLimitChange() {
    return this.type === 'AppAdminEvents::AdminAssignmentLimitChange';
  }

  get isAdminConversationAssignmentLimitChange() {
    return this.type === 'AppAdminEvents::AdminConversationAssignmentLimitChange';
  }

  get isAdminTicketAssignmentLimitChange() {
    return this.type === 'AppAdminEvents::AdminTicketAssignmentLimitChange';
  }

  get isAdminChannelLimitChange() {
    return this.type === 'AppAdminEvents::AdminChannelLimitChange';
  }

  get isTeamAssignmentLimitChange() {
    return this.type === 'AppAdminEvents::TeamAssignmentLimitChange';
  }

  get isFinOptIn() {
    return this.type === 'AppAdminEvents::FinOptIn';
  }

  get isIdvReminderDisabled() {
    return this.type === 'AppAdminEvents::AppIdentityVerificationReminderDisabled';
  }

  get isMessengerPlatformAvailabilityChange() {
    return this.type === 'AppAdminEvents::MessengerPlatformAvailabilityChange';
  }

  get isMessengerPlatformInactivityDisableEvent() {
    return this.type === 'AppAdminEvents::MessengerPlatformInactivityDisableEvent';
  }

  get isIpAllowlistSettingChange() {
    return this.type === 'AppAdminEvents::IpAllowlistSettingChange';
  }

  get isAdminMobileLoginSuccess() {
    return this.type === 'AppAdminEvents::AdminMobileLoginSuccess';
  }

  get isAdminMobileLoginFailure() {
    return this.type === 'AppAdminEvents::AdminMobileLoginFailure';
  }

  get isAdminNameChange() {
    return this.type === 'AppAdminEvents::AdminNameChange';
  }

  get isAdminEmailChange() {
    return this.type === 'AppAdminEvents::AdminEmailChange';
  }

  get isAdminPasswordChange() {
    return this.type === 'AppAdminEvents::AdminPasswordChange';
  }

  get isSeatRevoke() {
    return this.type === 'AppAdminEvents::SeatRevoke';
  }

  get isAdminChannelChange() {
    return this.type === 'AppAdminEvents::AdminChannelChange';
  }

  get isFinPersistedDataContextAttributeChange() {
    return this.type === 'AppAdminEvents::FinPersistedDataContextAttributeChange';
  }

  get isAiAgentCustomizationSettingsChange() {
    return this.type === 'AppAdminEvents::AiAgentCustomizationSettingsChange';
  }

  get isAiAgentIdentitySettingsChange() {
    return this.type === 'AppAdminEvents::AiAgentIdentitySettingsChange';
  }

  get isAiAgentMultilingualSettingsChange() {
    return this.type === 'AppAdminEvents::AiAgentMultilingualSettingsChange';
  }

  get isAiAgentGuidanceChange() {
    return this.type === 'AppAdminEvents::AiAgentGuidanceChange';
  }

  get isAiAgentFinPlaygroundChange() {
    return this.type === 'AppAdminEvents::AiAgentFinPlaygroundChange';
  }

  get isAiAgentPreviewTokenGenerated() {
    return this.type === 'AppAdminEvents::AiAgentPreviewTokenGenerated';
  }

  get isMessengerLanguageChange() {
    return this.type === 'AppAdminEvents::MessengerLanguageChange';
  }

  get isBrandCreation() {
    return this.type === 'AppAdminEvents::BrandCreation';
  }

  get isBrandUpdate() {
    return this.type === 'AppAdminEvents::BrandUpdate';
  }

  get isBrandDeletion() {
    return this.type === 'AppAdminEvents::BrandDeletion';
  }

  get isSeriesCreation() {
    return this.type === 'AppAdminEvents::SeriesCreation';
  }

  get isSeriesUpdate() {
    return this.type === 'AppAdminEvents::SeriesUpdate';
  }

  get isSeriesSettingsUpdate() {
    return this.type === 'AppAdminEvents::SeriesSettingsUpdate';
  }

  get isSeriesDeletion() {
    return this.type === 'AppAdminEvents::SeriesDeletion';
  }

  get isSeriesStatusChange() {
    return this.type === 'AppAdminEvents::SeriesStatusChange';
  }

  get isActionRiskAcceptedChange() {
    return this.type === 'AppAdminEvents::ActionRiskAcceptedChange';
  }

  get isActionPublished() {
    return this.type === 'AppAdminEvents::ActionPublished';
  }

  get isInternalAgentProposalApplied() {
    return this.type === 'AppAdminEvents::InternalAgentProposalApplied';
  }

  get isActionStatusChange() {
    return this.type === 'AppAdminEvents::ActionStatusChange';
  }

  get isActionUpdate() {
    return this.type === 'AppAdminEvents::ActionUpdate';
  }

  get isSelectionSetCreation() {
    return this.type === 'AppAdminEvents::SelectionSetCreation';
  }

  get isSelectionSetDeletion() {
    return this.type === 'AppAdminEvents::SelectionSetDeletion';
  }

  get isMessengerLaunchToConversationChange() {
    return this.type === 'AppAdminEvents::MessengerLaunchToConversationChange';
  }

  get isAliasModeChange() {
    return this.type === 'AppAdminEvents::AliasModeChange';
  }

  get isHippaComplianceSettingsChange() {
    return this.type === 'AppAdminEvents::HippaComplianceSettingsChange';
  }

  get isAiInboxTranslationSettingChange() {
    return this.type === 'AppAdminEvents::AiInboxTranslationSettingChange';
  }

  get isHideCsatFromAgentsSettingChange() {
    return this.type === 'AppAdminEvents::HideCsatFromAgentsSettingChange';
  }

  get isEmailSettingsChange() {
    return this.type === 'AppAdminEvents::EmailSettingsChange';
  }

  get isStripInboundEmailLinksChange() {
    return this.type === 'AppAdminEvents::StripInboundEmailLinksChange';
  }

  get isContentSuggestionsChange() {
    return this.type === 'AppAdminEvents::ContentSuggestionsChange';
  }

  get isCustomAuthenticationTokenCreation() {
    return this.type === 'AppAdminEvents::CustomAuthenticationTokenCreation';
  }

  get isPiiDataRevealed() {
    return this.type === 'AppAdminEvents::PiiDataRevealed';
  }

  get isDeveloperHubSecretRevealed() {
    return this.type === 'AppAdminEvents::DeveloperHubSecretRevealed';
  }

  get isDeveloperHubSecretCopied() {
    return this.type === 'AppAdminEvents::DeveloperHubSecretCopied';
  }

  get isCustomEmojiDeletion() {
    return this.type === 'AppAdminEvents::CustomEmojiDeletion';
  }

  get isCustomerFacingNameChange() {
    return this.type === 'AppAdminEvents::CustomerFacingNameChange';
  }

  get isAttachUploadsInlineSettingChange() {
    return this.type === 'AppAdminEvents::AttachUploadsInlineSettingChange';
  }

  get isUserCameraAttachmentsSettingChange() {
    return this.type === 'AppAdminEvents::UserCameraAttachmentsSettingChange';
  }

  get isUserMediaAttachmentsSettingChange() {
    return this.type === 'AppAdminEvents::UserMediaAttachmentsSettingChange';
  }

  get isUserFileAttachmentsSettingChange() {
    return this.type === 'AppAdminEvents::UserFileAttachmentsSettingChange';
  }

  get isUserGifsSettingChange() {
    return this.type === 'AppAdminEvents::UserGifsSettingChange';
  }

  get isUserVoiceNotesSettingChange() {
    return this.type === 'AppAdminEvents::UserVoiceNotesSettingChange';
  }

  get isUserConversationAttachmentsSettingChange() {
    return this.type === 'AppAdminEvents::UserConversationAttachmentsSettingChange';
  }

  get isFileUploadAudiencePredicatesSettingChange() {
    return this.type === 'AppAdminEvents::FileUploadAudiencePredicatesSettingChange';
  }

  get isTeammateGifsSettingChange() {
    return this.type === 'AppAdminEvents::TeammateGifsSettingChange';
  }

  get isAllowedAttachmentFiletypesSettingChange() {
    return this.type === 'AppAdminEvents::AllowedAttachmentFiletypesSettingChange';
  }

  get isUnassignUnsnoozedAtCapacitySettingChange() {
    return this.type === 'AppAdminEvents::UnassignUnsnoozedAtCapacitySettingChange';
  }

  get isUnassignUnsnoozedWhenAwaySettingChange() {
    return this.type === 'AppAdminEvents::UnassignUnsnoozedWhenAwaySettingChange';
  }

  get isAutomaticAwayModeSettingChange() {
    return this.type === 'AppAdminEvents::AutomaticAwayModeSettingChange';
  }

  get isFinAgentApiSettingsChange() {
    return this.type === 'AppAdminEvents::FinAgentApiSettingsChange';
  }

  get isQaMonitorCreation() {
    return this.type === 'AppAdminEvents::QaMonitorCreation';
  }

  get isQaMonitorUpdate() {
    return this.type === 'AppAdminEvents::QaMonitorUpdate';
  }

  get isQaMonitorArchive() {
    return this.type === 'AppAdminEvents::QaMonitorArchive';
  }

  get isQaScorecardCreation() {
    return this.type === 'AppAdminEvents::QaScorecardCreation';
  }

  get isQaScorecardUpdate() {
    return this.type === 'AppAdminEvents::QaScorecardUpdate';
  }

  get isQaScorecardArchive() {
    return this.type === 'AppAdminEvents::QaScorecardArchive';
  }

  get isScimGroupCreation() {
    return this.type === 'AppAdminEvents::ScimGroupCreation';
  }

  get isScimGroupUpdate() {
    return this.type === 'AppAdminEvents::ScimGroupUpdate';
  }

  get isScimGroupDeletion() {
    return this.type === 'AppAdminEvents::ScimGroupDeletion';
  }

  get isMacroCreation() {
    return this.type === 'AppAdminEvents::MacroCreation';
  }

  get isMacroUpdate() {
    return this.type === 'AppAdminEvents::MacroUpdate';
  }

  get isMacroDeletion() {
    return this.type === 'AppAdminEvents::MacroDeletion';
  }

  get isMcpServerCreated() {
    return this.type === 'AppAdminEvents::McpServerCreated';
  }

  get isMcpServerUpdated() {
    return this.type === 'AppAdminEvents::McpServerUpdated';
  }

  get isMcpServerDeleted() {
    return this.type === 'AppAdminEvents::McpServerDeleted';
  }

  get isMcpServerEnabled() {
    return this.type === 'AppAdminEvents::McpServerEnabled';
  }

  get isMcpServerDisabled() {
    return this.type === 'AppAdminEvents::McpServerDisabled';
  }

  get isMcpServerOauthConnected() {
    return this.type === 'AppAdminEvents::McpServerOauthConnected';
  }

  get isMcpServerOauthDisconnected() {
    return this.type === 'AppAdminEvents::McpServerOauthDisconnected';
  }

  get isMcpActionAdded() {
    return this.type === 'AppAdminEvents::McpActionAdded';
  }

  get isMcpActionsBatchAdded() {
    return this.type === 'AppAdminEvents::McpActionsBatchAdded';
  }

  get isMcpActionUpdated() {
    return this.type === 'AppAdminEvents::McpActionUpdated';
  }

  get isMcpActionRemoved() {
    return this.type === 'AppAdminEvents::McpActionRemoved';
  }

  get isScimAdminProfileUpdate() {
    return this.type === 'AppAdminEvents::ScimAdminProfileUpdate';
  }

  get isScimAdminPasswordUpdate() {
    return this.type === 'AppAdminEvents::ScimAdminPasswordUpdate';
  }

  get isScimSystemEnabled() {
    return this.type === 'AppAdminEvents::ScimSystemEnabled';
  }

  get isScimSystemDisabled() {
    return this.type === 'AppAdminEvents::ScimSystemDisabled';
  }

  get isScimSettingsChange() {
    return this.type === 'AppAdminEvents::ScimSettingsChange';
  }

  get isScimGroupMembershipChange() {
    return this.type === 'AppAdminEvents::ScimGroupMembershipChange';
  }

  get isScimAdminRoleChange() {
    return this.type === 'AppAdminEvents::ScimAdminRoleChange';
  }

  get isScimGroupsBulkUpdate() {
    return this.type === 'AppAdminEvents::ScimGroupsBulkUpdate';
  }

  get isScimAdminBulkRoleAssignment() {
    return this.type === 'AppAdminEvents::ScimAdminBulkRoleAssignment';
  }

  get hasScimAdminRoleChangeDetails() {
    return this.isScimAdminRoleChange && (this.value?.before || this.value?.after);
  }

  get hasScimGroupMembershipChangeDetails() {
    return this.isScimGroupMembershipChange;
  }

  get hasScimSettingsChangeDetails() {
    return this.isScimSettingsChange && (this.value?.before || this.value?.after);
  }

  get hasScimAdminProfileUpdateDetails() {
    return this.isScimAdminProfileUpdate && (this.value?.before || this.value?.after);
  }

  get hasScimGroupsBulkUpdateDetails() {
    return this.isScimGroupsBulkUpdate && this.value?.groups && this.value.groups.length >= 2;
  }

  get hasScimAdminBulkRoleAssignmentDetails() {
    return (
      this.isScimAdminBulkRoleAssignment && this.value?.admins && this.value.admins.length >= 2
    );
  }

  get isAiCategoryChange() {
    return this.type === 'AppAdminEvents::AiCategoryChange';
  }

  get isConversationAttributeArchive() {
    return this.type === 'AppAdminEvents::ConversationAttributeArchive';
  }

  get isEscalationRuleChange() {
    return this.type === 'AppAdminEvents::EscalationRuleChange';
  }

  get isMessengerApiSecretCreation() {
    return this.type === 'AppAdminEvents::MessengerApiSecretCreation';
  }

  get isMessengerApiSecretDeletion() {
    return this.type === 'AppAdminEvents::MessengerApiSecretDeletion';
  }

  get isWorkspaceDeletionRequest() {
    return this.type === 'AppAdminEvents::WorkspaceDeletionRequest';
  }

  get hasGenericBeforeAfterDetails() {
    if (
      !this.isSecuritySettingChange &&
      !this.isMacroUpdate &&
      !this.isAutomaticAwayModeSettingChange &&
      !this.isContentRedactionRuleUpdate
    ) {
      return false;
    }

    let { before, after } = this.value || {};

    return before !== undefined && after !== undefined && !isEqual(before, after);
  }

  get isLeadToUserMerge() {
    return this.type === 'AppAdminEvents::LeadToUserMerge';
  }

  get isShopifyRefundOrder() {
    return this.type === 'AppAdminEvents::ShopifyRefundOrder';
  }

  get isShopifyReplacementOrder() {
    return this.type === 'AppAdminEvents::ShopifyReplacementOrder';
  }

  get isShopifyGiftCardCompensation() {
    return this.type === 'AppAdminEvents::ShopifyGiftCardCompensation';
  }

  get isShopifyAddReplacementItem() {
    return this.type === 'AppAdminEvents::ShopifyAddReplacementItem';
  }

  get isCsvImportCreation() {
    return this.type === 'AppAdminEvents::CsvImportCreation';
  }

  get isCsvImportCompletion() {
    return this.type === 'AppAdminEvents::CsvImportCompletion';
  }

  get isConversationDeletionScheduleCreation() {
    return this.type === 'AppAdminEvents::ConversationDeletionScheduleCreation';
  }

  get isConversationDeletionScheduleStateChange() {
    return this.type === 'AppAdminEvents::ConversationDeletionScheduleStateChange';
  }

  get isConversationDeletionScheduleUpdate() {
    return this.type === 'AppAdminEvents::ConversationDeletionScheduleUpdate';
  }

  get isConversationDeletionScheduleDeletion() {
    return this.type === 'AppAdminEvents::ConversationDeletionScheduleDeletion';
  }

  get isContentRedactionRuleCreation() {
    return this.type === 'AppAdminEvents::ContentRedactionRuleCreation';
  }

  get isContentRedactionRuleUpdate() {
    return this.type === 'AppAdminEvents::ContentRedactionRuleUpdate';
  }

  get isContentRedactionRuleDeletion() {
    return this.type === 'AppAdminEvents::ContentRedactionRuleDeletion';
  }

  get isConversationHardDeletionEnqueued() {
    return this.type === 'AppAdminEvents::ConversationHardDeletionEnqueued';
  }

  get isConversationRedactionEnqueued() {
    return this.type === 'AppAdminEvents::ConversationRedactionEnqueued';
  }

  get isConversationSlaChange() {
    return this.type === 'AppAdminEvents::ConversationSlaChange';
  }

  get isSlackOauthFreshInstall() {
    return this.type === 'AppAdminEvents::SlackOauthFreshInstall';
  }

  get isSlackOauthReinstall() {
    return this.type === 'AppAdminEvents::SlackOauthReinstall';
  }

  get isSlackOauthReauthentication() {
    return this.type === 'AppAdminEvents::SlackOauthReauthentication';
  }

  get isSlackUserTokenCreation() {
    return this.type === 'AppAdminEvents::SlackUserTokenCreation';
  }
}
